import { useCallback, useMemo } from "react";
import { useLivePreview } from "@payloadcms/live-preview-react";
import { getPayloadCmsUrl } from "src/api/payload/client";

/**
 * Merges a single live-preview document into a hub card list (upsert by id).
 *
 * @param {Object[]} items - The hub's current card list
 * @param {boolean} enabled - true when isPayloadSource(moduleName) for this hub
 * @param {string} [locale] - `en` | `fr`
 * @param {Object} config
 * @param {number} config.depth - Payload query depth for the live-preview relation fetch
 * @param {(liveDoc: Object, locale?: string) => Object} config.mapDoc - Maps the raw Payload doc into this module's card shape
 * @param {Function} config.requestHandler - Payload live-preview request handler for this module's collection
 * @param {(item: Object) => unknown} config.getItemId - Returns an item's unique id/key, used to find and replace it in the list
 * @returns {{
 *   items: Object[],
 *   isLivePreview: boolean,
 *   livePreviewId: unknown,
 *   isLoadingLivePreview: boolean,
 * }}
 */
export function useHubListLivePreview(items, enabled, locale, config) {
  const { depth, mapDoc, requestHandler, getItemId } = config;
  const initialDoc = useMemo(() => ({}), []);
  const stableRequestHandler = useCallback(requestHandler, [requestHandler]);

  const { data: liveDoc, isLoading } = useLivePreview({
    initialData: initialDoc,
    serverURL: getPayloadCmsUrl(),
    depth,
    requestHandler: stableRequestHandler,
  });

  const liveCard = useMemo(() => {
    if (!enabled) return null;
    return mapDoc(liveDoc, locale);
  }, [enabled, liveDoc, locale, mapDoc]);
  const liveId = enabled && liveCard ? getItemId(liveCard) : null;

  const mergedItems = useMemo(() => {
    if (!enabled || liveId == null) return items;

    const index = items.findIndex(
      (item) => String(getItemId(item)) === String(liveId)
    );
    if (index >= 0) {
      const next = [...items];
      next[index] = { ...next[index], ...liveCard };
      return next;
    }

    return [...items, liveCard];
  }, [items, liveCard, liveId, enabled, getItemId]);

  return {
    items: mergedItems,
    isLivePreview: enabled && liveId != null,
    livePreviewId: enabled ? liveId : undefined,
    isLoadingLivePreview: enabled && isLoading,
  };
}
